import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Map, { Source, Layer, Marker, Popup, NavigationControl } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { t } from 'i18next';
import { SelectButton } from 'primereact';
import { DirectionType } from '../../model/enum/DirectionType';
import finishIcon from '../../components/base/finish.svg';
import startIcon from '../../components/base/start.svg';
import { withTranslation } from 'react-i18next';
import { Card } from 'primereact/card';

export const FullScreenMapComponent = ({ initialRoutes }) => {
    const [routes, setRoutes] = useState(initialRoutes.filter(route => route.direction === DirectionType.TO_WORK));
    const [direction, setDirection] = useState({ name: t('DirectionType.TO_WORK'), type: 'TO_WORK' });
    const [selectedRoute, setSelectedRoute] = useState(null);
    const [popupPosition, setPopupPosition] = useState(null);
    const mapRef = useRef();

    useEffect(() => {
        // Aktualizuj trasy gdy się zmienią
        if (initialRoutes && initialRoutes.length > 0) {
            const filteredRoutes = initialRoutes.filter(route => {
                if (direction.type === DirectionType.UNSET) {
                    return route.direction !== DirectionType.TO_WORK && route.direction !== DirectionType.FROM_WORK;
                } else {
                    return route.direction === direction.type;
                }
            });
            setRoutes(filteredRoutes);
        }
    }, [initialRoutes, direction]);

    const directionText = (direction) => {
        switch (direction) {
            case DirectionType.FROM_WORK:
                return t('DirectionType.FROM_WORK');
            case DirectionType.TO_WORK:
                return t('DirectionType.TO_WORK');
            default:
                return t('DirectionType.UNSET');
        }
    };

    const directions = [
        { name: t('DirectionType.FROM_WORK'), type: DirectionType.FROM_WORK },
        { name: t('DirectionType.TO_WORK'), type: DirectionType.TO_WORK },
        { name: t('DirectionType.UNSET'), type: DirectionType.UNSET }
    ];

    const calculateBounds = (routes) => {
        if (!routes || routes.length === 0) return null;
        
        const coordinates = routes.flatMap(route => route.coordinates);
        if (coordinates.length === 0) return null;
        
        const longitudes = coordinates.map(c => c.longitude);
        const latitudes = coordinates.map(c => c.latitude);
        const minLon = Math.min(...longitudes);
        const maxLon = Math.max(...longitudes);
        const minLat = Math.min(...latitudes) - 0.01;
        const maxLat = Math.max(...latitudes) + 0.01;
        return [[minLon, minLat], [maxLon, maxLat]];
    };

    const updateRoutes = (e) => {
        const type = e.value?.type;
        const routesToInititate = initialRoutes.filter(route => {
            if (type === DirectionType.UNSET) {
                return route.direction !== DirectionType.TO_WORK && route.direction !== DirectionType.FROM_WORK;
            } else {
                return route.direction === type;
            }
        });
        setDirection(e.value);
        setRoutes(routesToInititate);
    };

    let userRoutesBounds = null;
    if (routes && routes.length > 0) {
        userRoutesBounds = calculateBounds(routes);
    }

    const handleRouteClick = (route, coordinates) => {
        setSelectedRoute(route);
        setPopupPosition(coordinates);
    };

    const getRouteIndexFromSourceParam = (feature) => {
        const str = feature.source;
        const number = str.match(/\d+/);
        return number;
    }

    const handleMouseMove = (event) => {
        const map = mapRef.current?.getMap();
        if (!map) return;
        
        const features = map.queryRenderedFeatures(event.point, {
            layers: routes.map((_, i) => `route-layer-${i}`)
        });
        
        if (features.length > 0) {
            const hoveredFeature = features[0];
            const routeIndex = getRouteIndexFromSourceParam(hoveredFeature);
            if (routeIndex && routes[routeIndex]) {
                setSelectedRoute(routes[routeIndex]);
                setPopupPosition([event.lngLat.lng, event.lngLat.lat]);
                return;
            }
        }
        setSelectedRoute(null);
        setPopupPosition(null);
    };

    return (
        <Card className="dashboard-card-map mt-1">
            
                <SelectButton
                    optionLabel="name"
                    value={direction}
                    options={directions}
                    onChange={updateRoutes}
                />
            
            <Map
                ref={mapRef}
                initialViewState={{
                    longitude: userRoutesBounds ? (userRoutesBounds[0][0] + userRoutesBounds[1][0]) / 2 : 19.107532,
                    latitude: userRoutesBounds ? (userRoutesBounds[0][1] + userRoutesBounds[1][1]) / 2 : 52.037049,
                    zoom: userRoutesBounds ? 12 : 6
                }}
                style={{ height: '100vh', zIndex: '100' }}
                mapStyle={process.env.REACT_APP_MAP_STYLE}
                bounds={userRoutesBounds}
                onMouseMove={handleMouseMove}
            >
                <NavigationControl position="top-right" />
                {routes.map((route, routeIndex) => {
                    if (!route.coordinates || route.coordinates.length === 0) return null;
                    
                    const startPoint = route.coordinates[0];
                    const lastPoint = route.coordinates[route.coordinates.length - 1];
                    const routeCoordinates = route.coordinates.map(coo => [coo.longitude, coo.latitude]);
                    
                    return (
                        <div key={routeIndex}>
                            <Source
                                id={`route-source-${routeIndex}`}
                                type="geojson"
                                data={{
                                    type: "FeatureCollection",
                                    features: [
                                        {
                                            id: routeIndex,
                                            type: "Feature",
                                            geometry: {
                                                type: "LineString",
                                                coordinates: routeCoordinates
                                            },
                                        }
                                    ]
                                }}
                            >
                                <Layer
                                    id={`route-layer-${routeIndex}`}
                                    type="line"
                                    paint={{
                                        'line-color': '#0000FF',
                                        'line-width': 4
                                    }}
                                />
                            </Source>
                            <Marker
                                longitude={startPoint.longitude}
                                latitude={startPoint.latitude}
                                anchor="bottom"
                                offsetLeft={-16}
                                offsetTop={-32}
                                onClick={() => handleRouteClick(route, [startPoint.longitude, startPoint.latitude])}
                            >
                                <img src={startIcon} alt="Start Icon" width={32} height={32} />
                            </Marker>
                            <Marker
                                longitude={lastPoint.longitude}
                                latitude={lastPoint.latitude}
                                anchor="bottom"
                                offsetLeft={-16}
                                offsetTop={-32}
                                onClick={() => handleRouteClick(route, [lastPoint.longitude, lastPoint.latitude])}
                            >
                                <img src={finishIcon} alt="Finish Icon" width={32} height={32} />
                            </Marker>
                            {selectedRoute === route && popupPosition && (
                                <Popup
                                    longitude={popupPosition[0]}
                                    latitude={popupPosition[1]}
                                    closeButton={false}
                                    closeOnClick={false}
                                    anchor="top"
                                >
                                    <div className="row">
                                        <div className="col-12">
                                            <b>{t('global.user')}:</b> {route.user?.fullName || 'Nieznany użytkownik'}
                                        </div>
                                        <div className="col-12">
                                            <b>{t('global.transportName')}:</b> {route.transport?.name || 'Nieznany transport'}
                                        </div>
                                        <div className="col-12">
                                            <b>{t('global.direction')}:</b> {directionText(route.direction)}
                                        </div>
                                    </div>
                                </Popup>
                            )}
                        </div>
                    );
                })}
            </Map>
        </Card>
    );
};

FullScreenMapComponent.defaultProps = {
    initialRoutes: [],
};

FullScreenMapComponent.propTypes = {
    initialRoutes: PropTypes.array.isRequired,
};

export default withTranslation()(FullScreenMapComponent);
